import React from "react";

import InputPhoneSADC from "@/components/auth/InputPhoneSADC.jsx";
import InputCountrySADC from "@/components/fields/InputCountrySADC.jsx";
import OnboardingSection from "@/components/onboarding/OnboardingSection.jsx";
import OnboardingTopBar from "@/components/onboarding/OnboardingTopBar.jsx";
import Button from "@/components/ui/Button.jsx";
import Checkbox from "@/components/ui/Checkbox.jsx";
import Divider from "@/components/ui/Divider.jsx";
import Radio from "@/components/ui/Radio.jsx";
import SettingsCard from "@/sot/primitives/SettingsCard.jsx";
import TextField56 from "@/sot/primitives/TextField56.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import { saveResearchFirstUseStep } from "@/services/stagingAccessService.js";
import { splitE164ToCcLocal, toE164FromCcLocal } from "@/utils/phone.js";

const STAGES = [
  "research_basic_details",
  "research_organisation",
  "research_work",
  "research_governance",
];

export const RESEARCH_ROLE_TYPES = [
  "researcher",
  "postgraduate_student",
  "postdoctoral_fellow",
  "programme_staff",
  "public_health_professional",
  "community_health_worker",
  "research_field_staff",
  "other",
];

export const RESEARCH_INTERESTS = [
  "digital_health",
  "hiv_sti",
  "sexual_reproductive_health",
  "telehealth",
  "public_health",
  "population_longitudinal_research",
  "implementation_science",
  "data_governance",
  "community_field_workflows",
  "other",
];

function normalizeResearchPhone(value) {
  return String(value || "").trim().replace(/[\s().-]+/g, "");
}

function isValidResearchPhone(value) {
  return /^\+[1-9]\d{7,14}$/.test(normalizeResearchPhone(value));
}

export function ResearchPhoneInput({ value = "", onChange, tone = "surface", label = "Phone", countryCodeLabel = "Country code" }) {
  const initial = splitE164ToCcLocal(value);
  const [cc, setCc] = React.useState(initial?.cc || "+27");
  const [local, setLocal] = React.useState(initial?.local || "");

  React.useEffect(() => {
    const parts = splitE164ToCcLocal(value);
    if (!parts) return;
    setCc(parts.cc);
    setLocal(parts.local);
  }, [value]);

  const publish = React.useCallback((nextCc, nextLocal) => {
    const digits = String(nextLocal || "").replace(/\D/g, "");
    onChange?.(toE164FromCcLocal(nextCc, digits) || `${nextCc}${digits}`);
  }, [onChange]);

  return (
    <InputPhoneSADC
      value={local}
      onChange={(nextLocal) => {
        setLocal(nextLocal);
        publish(cc, nextLocal);
      }}
      cc={cc}
      onChangeCc={(nextCc) => {
        setCc(nextCc);
        publish(nextCc, local);
      }}
      countryCodeLabel={countryCodeLabel}
      numberLabel={label}
      tone={tone}
    />
  );
}

export function createResearchContextForm(context = null) {
  return {
    first_name: context?.first_name || "",
    last_name: context?.last_name || "",
    phone: context?.phone || "",
    organisation: context?.organisation || "",
    department_unit: context?.department_unit || "",
    country: context?.country || "",
    role_type: context?.role_type || "",
    role_type_other: context?.role_type_other || "",
    areas_of_interest: Array.isArray(context?.areas_of_interest)
      ? context.areas_of_interest
      : [],
    interest_other: context?.interest_other || "",
    acknowledged: context?.governance_acknowledged === true,
  };
}

function SelectionSurface({ children, ariaLabel = "" }) {
  return (
    <SettingsCard fullWidth role="group" aria-label={ariaLabel}>
      <div>{children}</div>
    </SettingsCard>
  );
}

function SelectionRow({ onSelect, control, children, divider = false }) {
  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className="flex w-full cursor-pointer items-center"
        onClick={onSelect}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect?.();
          }
        }}
        style={{ WebkitTapHighlightColor: "transparent", touchAction: "manipulation" }}
      >
        <span className="flex-1 py-4 pl-5 pr-4 text-left text-body-large text-on-surface">{children}</span>
        <span className="mr-4">{control}</span>
      </div>
      {divider ? <Divider inset variant="outlineVariant" /> : null}
    </>
  );
}

export default function ResearchPublicHealthOnboarding({
  stage,
  context = null,
  membership = null,
  onSaved,
  onBack,
  onEditProfile,
  onContinueToAccount,
}) {
  const { t } = useLanguage();
  const [form, setForm] = React.useState(() => createResearchContextForm(context));
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!context) return;
    setForm(createResearchContextForm(context));
  }, [context]);

  const setValue = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const toggleInterest = (key) => {
    setForm((current) => ({
      ...current,
      areas_of_interest: current.areas_of_interest.includes(key)
        ? current.areas_of_interest.filter((entry) => entry !== key)
        : [...current.areas_of_interest, key],
    }));
  };

  const stepIndex = Math.max(0, STAGES.indexOf(stage));

  const validationError = () => {
    if (stage === "research_basic_details" && (!form.first_name.trim() || !form.last_name.trim())) {
      return t("researchOnboarding.errors.basic", "Enter your first and last name.");
    }
    if (stage === "research_basic_details" && !isValidResearchPhone(form.phone)) {
      return t("researchOnboarding.errors.phone", "Enter a valid phone number with country code.");
    }
    if (stage === "research_organisation" && !form.organisation.trim()) {
      return t("researchOnboarding.errors.organisation", "Enter your organisation or institution.");
    }
    if (stage === "research_organisation" && !form.department_unit.trim()) {
      return t("researchOnboarding.errors.department", "Enter your department or unit.");
    }
    if (stage === "research_organisation" && !form.country.trim()) {
      return t("researchOnboarding.errors.country", "Choose your country.");
    }
    if (stage === "research_work" && form.role_type === "other" && !form.role_type_other.trim()) {
      return t("researchOnboarding.errors.roleOther", "Describe your role to save the Other selection.");
    }
    if (stage === "research_work" && form.areas_of_interest.includes("other") && !form.interest_other.trim()) {
      return t("researchOnboarding.errors.interestOther", "Describe the other area to save that selection.");
    }
    if (stage === "research_governance" && !form.acknowledged) {
      return t("researchOnboarding.errors.governance", "Acknowledge the staging governance conditions to continue.");
    }
    return "";
  };

  const submit = async () => {
    if (saving || !STAGES.includes(stage)) return;
    const clientError = validationError();
    if (clientError) {
      setError(clientError);
      return;
    }
    setSaving(true);
    setError("");
    try {
      const result = await saveResearchFirstUseStep({ stage, data: form });
      onSaved?.(result);
    } catch (nextError) {
      const code = String(nextError?.code || nextError?.message || "");
      const message = code.includes("basic_details")
        ? t("researchOnboarding.errors.basic", "Enter your first and last name.")
        : code.includes("phone")
          ? t("researchOnboarding.errors.phone", "Enter a valid phone number with country code.")
        : code.includes("organisation")
          ? t("researchOnboarding.errors.organisation", "Enter your organisation or institution.")
          : code.includes("department")
            ? t("researchOnboarding.errors.department", "Enter your department or unit.")
            : code.includes("country")
              ? t("researchOnboarding.errors.country", "Choose your country.")
          : code.includes("role_required")
            ? t("researchOnboarding.errors.roleOther", "Describe your role to save the Other selection.")
            : code.includes("interests")
              ? t("researchOnboarding.errors.interestOther", "Describe the other area to save that selection.")
              : code.includes("governance")
                ? t("researchOnboarding.errors.governance", "Acknowledge the staging governance conditions to continue.")
                : t("researchOnboarding.errors.save", "AfriMed™ could not save this onboarding step. Please try again.");
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const goBack = async () => {
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      await onBack?.();
    } catch {
      setError(t("researchOnboarding.errors.back", "AfriMed™ could not return to the previous step. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  if (stage === "membership_pending") {
    return (
      <div className="min-h-dvh flex flex-col">
        <OnboardingTopBar step={5} total={5} onBack={goBack} />
        <OnboardingSection
          header={
            <div className="space-y-2">
              <h1 className="text-title-large text-on-surface">
                {t("researchOnboarding.pending.title", "Research • Public health account ready")}
              </h1>
              <p className="text-body-medium text-on-surface-var">
                {t(
                  "researchOnboarding.pending.body",
                  "Your AfriMed™ Research • Public health account is ready. Institution access will appear once your programme membership has been assigned.",
                )}
              </p>
            </div>
          }
          footer={
            <>
              <Button size="lg" onClick={onContinueToAccount}>{t("researchOnboarding.shared.continue", "Continue")}</Button>
              <Button size="lg" variant="text" onClick={onEditProfile}>{t("researchOnboarding.pending.edit", "Review your details")}</Button>
            </>
          }
          footerHeight={148}
          footerShellClassName="bg-[rgb(var(--clr-surface-container-high))] shadow-[0_-16px_32px_rgba(15,23,42,0.14)] md:bg-transparent md:shadow-none"
        >
          <div className="w-full">
            <SettingsCard fullWidth className="p-4 text-body-medium text-on-surface-var">
              {t(
                "researchOnboarding.pending.boundary",
                "No Institution, clinical, Regional Health, programme-administration, or dataset authority has been assigned. Access is activated only through governed programme membership.",
              )}
            </SettingsCard>
          </div>
        </OnboardingSection>
      </div>
    );
  }

  if (stage === "completed" && membership?.destination) return null;

  let title = "";
  let intro = t("researchOnboarding.shared.intro", "Tell us enough to orient your controlled AfriMed™ staging experience. These details do not grant product authority.");
  let body = null;
  if (stage === "research_basic_details") {
    title = t("researchOnboarding.basic.title", "Basic identity");
    body = (
      <div className="space-y-3">
        <TextField56 label={t("researchOnboarding.basic.firstName", "First name")} value={form.first_name} onChange={(value) => setValue("first_name", value)} />
        <TextField56 label={t("researchOnboarding.basic.lastName", "Last name")} value={form.last_name} onChange={(value) => setValue("last_name", value)} />
        <ResearchPhoneInput
          countryCodeLabel={t("researchOnboarding.basic.countryCode", "Country code")}
          label={t("researchOnboarding.basic.phone", "Phone")}
          value={form.phone}
          onChange={(value) => setValue("phone", value)}
        />
      </div>
    );
  } else if (stage === "research_organisation") {
    title = t("researchOnboarding.organisation.title", "Organisation • context");
    body = (
      <div className="space-y-3">
        <TextField56 label={t("researchOnboarding.organisation.name", "Organisation • institution")} value={form.organisation} onChange={(value) => setValue("organisation", value)} />
        <TextField56 label={t("researchOnboarding.organisation.department", "Department • unit")} value={form.department_unit} onChange={(value) => setValue("department_unit", value)} />
        <InputCountrySADC
          label={t("researchOnboarding.organisation.country", "Country")}
          value={form.country}
          onChange={(value) => setValue("country", value)}
          tone="surface"
        />
      </div>
    );
  } else if (stage === "research_work") {
    title = t("researchOnboarding.work.title", "Your work");
    intro = t("researchOnboarding.work.intro", "Share as much as is useful for orientation. These optional details do not grant access or authority and can be completed later.");
    body = (
      <div className="space-y-5">
        <section className="space-y-2">
          <h2 className="text-title-medium text-on-surface">{t("researchOnboarding.role.heading", "Role")}</h2>
          <SelectionSurface ariaLabel={t("researchOnboarding.role.heading", "Role")}>
            {RESEARCH_ROLE_TYPES.map((key, index) => (
              <SelectionRow
                key={key}
                divider={index < RESEARCH_ROLE_TYPES.length - 1}
                onSelect={() => setValue("role_type", form.role_type === key ? "" : key)}
                control={<Radio variant="afrimed" name={t(`researchOnboarding.role.options.${key}`, key)} checked={form.role_type === key} onChange={() => setValue("role_type", form.role_type === key ? "" : key)} />}
              >
                {t(`researchOnboarding.role.options.${key}`, key)}
              </SelectionRow>
            ))}
          </SelectionSurface>
          {form.role_type === "other" ? (
            <TextField56 label={t("researchOnboarding.role.other", "Describe your role")} value={form.role_type_other} onChange={(value) => setValue("role_type_other", value)} />
          ) : null}
        </section>

        <section className="space-y-2">
          <h2 className="text-title-medium text-on-surface">{t("researchOnboarding.interests.heading", "Areas of interest")}</h2>
          <SelectionSurface ariaLabel={t("researchOnboarding.interests.heading", "Areas of interest")}>
            {RESEARCH_INTERESTS.map((key, index) => (
              <SelectionRow
                key={key}
                divider={index < RESEARCH_INTERESTS.length - 1}
                onSelect={() => toggleInterest(key)}
                control={<Checkbox ariaLabel={t(`researchOnboarding.interests.options.${key}`, key)} checked={form.areas_of_interest.includes(key)} onChange={() => toggleInterest(key)} />}
              >
                {t(`researchOnboarding.interests.options.${key}`, key)}
              </SelectionRow>
            ))}
          </SelectionSurface>
          {form.areas_of_interest.includes("other") ? (
            <TextField56 label={t("researchOnboarding.interests.other", "Describe the other area")} value={form.interest_other} onChange={(value) => setValue("interest_other", value)} />
          ) : null}
        </section>
      </div>
    );
  } else {
    title = t("researchOnboarding.governance.title", "Controlled staging acknowledgement");
    body = (
      <SettingsCard fullWidth className="p-4">
        <ul className="list-disc space-y-2 pl-5 text-left text-body-medium text-on-surface-var">
          <li>{t("researchOnboarding.governance.synthetic", "Use synthetic or evaluation data where applicable.")}</li>
          <li>{t("researchOnboarding.governance.patientData", "This journey gives no automatic access to patient-identifying records.")}</li>
          <li>{t("researchOnboarding.governance.membership", "Access remains governed by Institution and programme membership.")}</li>
          <li>{t("researchOnboarding.governance.auditable", "Controlled staging activity is auditable.")}</li>
        </ul>
        <div className="mt-4 flex cursor-pointer items-start gap-3 rounded-[12px] bg-[var(--state-primary-08)] p-3 text-left" onClick={() => setValue("acknowledged", !form.acknowledged)}>
          <Checkbox className="mt-0.5 shrink-0" ariaLabel={t("researchOnboarding.governance.acknowledge", "I understand and acknowledge these controlled staging conditions.")} checked={form.acknowledged} onChange={(checked) => setValue("acknowledged", checked)} />
          <span className="text-body-medium text-on-surface">
            {t("researchOnboarding.governance.acknowledge", "I understand and acknowledge these controlled staging conditions.")}
          </span>
        </div>
      </SettingsCard>
    );
  }

  const workFooter = stage === "research_work" ? (
    <>
      <Button size="lg" disabled={saving} onClick={submit}>{saving ? t("researchOnboarding.shared.saving", "Saving...") : t("researchOnboarding.shared.continue", "Continue")}</Button>
      <Button size="lg" variant="text" disabled={saving} onClick={submit}>{t("researchOnboarding.work.completeLater", "Complete later")}</Button>
    </>
  ) : (
    <Button size="lg" disabled={saving} onClick={submit}>{saving ? t("researchOnboarding.shared.saving", "Saving...") : t("researchOnboarding.shared.continue", "Continue")}</Button>
  );

  return (
    <div className="min-h-dvh flex flex-col">
      <OnboardingTopBar step={stepIndex + 1} total={5} onBack={goBack} />
      <OnboardingSection
        header={
          <div className="space-y-2">
            <h1 className="text-title-large text-on-surface">{title}</h1>
            <p className="text-body-medium text-on-surface-var">{intro}</p>
          </div>
        }
        footer={workFooter}
        footerHeight={stage === "research_work" ? 148 : 96}
        footerShellClassName="bg-[rgb(var(--clr-surface-container-high))] shadow-[0_-16px_32px_rgba(15,23,42,0.14)] md:bg-transparent md:shadow-none"
      >
        <div className="w-full space-y-4">
          {body}
          {error ? <div role="alert" className="rounded-[12px] bg-[rgba(var(--clr-error),0.10)] p-3 text-body-medium text-[rgb(var(--clr-error))]">{error}</div> : null}
        </div>
      </OnboardingSection>
    </div>
  );
}
